import React, { useState, useEffect, useContext } from 'react';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { confirmAlert } from 'react-confirm-alert';
import _ from 'lodash';
import moment from 'moment';
import { navigate } from '@reach/router';
import Categories from './Categories';
import Items from './Items';
import Tiles from './Tiles';
import OwnedTiles from './OwnedTiles';
import Alert from './Alert';
import Confirm from './Confirm';
import BoomerBucks from './BoomerBucks';
import DrawBoomer from './DrawBoomer';
import SpecialStore from './SpecialStore';
import SpotlightLeft from '../../../images/spotlight-left.png';
import SpotlightRight from '../../../images/spotlight-right.png';
import Coins from '../../Dashboard/images/gem.svg';
import Home from '../../../images/new_dashboard/menu-home.svg';
import BoomerItems from '../../../images/boomer_items.png';
import BoomerBoutique from '../../../images/boomer_boutique.png';
import Backdrop from '../../../images/backdrop.png';
import SmallLoader from '../../../images/small-loader.svg'

import {
    BODY,
    BOOMER_FALLBACK_DATA,
    isWearing,
    previewItem,
    previewTile,
    removeItem,
    sortItems
} from '../../../services/utils/boomer';

import {
    BOOMER_STORE_QUERY,
    SAVE_BOOMER_ITEMS,
    BUY_BOOMER_ITEM,
    BUY_BOOMER_ITEM_BY_ID,
    GET_BOOMER_QUERY,
    GEMS_QUERY,
    SPECIAL_STORE_OPENING
} from '../services/graphql';

import { AuthContext } from '../../../services/auth/AuthProvider';

const getStockTime = (endDate, now) => {
    if (!endDate || !endDate.isValid()) {
        return '000000';
    }

    const remainingMinutes = Math.min(
        Math.max(endDate.diff(moment.utc(now), 'minutes'), 0),
        100 * 24 * 60 - 1
    );
    const days = Math.floor(remainingMinutes / (24 * 60));
    const hours = Math.floor((remainingMinutes % (24 * 60)) / 60);
    const minutes = remainingMinutes % 60;
    const twoDigits = value => `0${value}`.slice(-2);

    return `${twoDigits(days)}${twoDigits(hours)}${twoDigits(minutes)}`;
};

const Container = ({ data: { boomerAvatarJson, availableGems, avatarItems, backgroundImageUrl } }) => {
    const client = useApolloClient();
    const avatarData = boomerAvatarJson !== '[]' ? JSON.parse(boomerAvatarJson) : { ...BOOMER_FALLBACK_DATA };
    const savedBackdrop = avatarItems.find(item => item.type === 'backdrop' && (Array.isArray(item.url) ? item.url.includes(backgroundImageUrl) : item.url === backgroundImageUrl));
    avatarData.items = (avatarData.items || []).filter(item => item && item.type !== 'backdrop');
    if (savedBackdrop) {
        avatarData.items.push({ ...savedBackdrop, tile: Array.isArray(savedBackdrop.url) ? savedBackdrop.url.indexOf(backgroundImageUrl) + 1 : 1 });
    }
    const [containerState, setContainerState] = useState({
        boomerImgData: null,
        boomerReset: avatarData,
        boomerWearing: avatarData,
        inventoryType: 'store',
        saving: false,
        selectedCategory: BODY,
        selectedItem: null
    });
    const [allItems, setAllItems] = useState(null)
    const [specialStoreNow, setSpecialStoreNow] = useState(Date.now());

    const { currentUser, setCurrentUser } = useContext(AuthContext);
    const showingSpecialStore = containerState.inventoryType === 'store' && containerState.selectedCategory === 'special_store';
    const {
        data: specialStoreData,
        error: specialStoreError,
        loading: specialStoreLoading
    } = useQuery(SPECIAL_STORE_OPENING, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        skip: !showingSpecialStore
    });

    useEffect(() => {
        if (!showingSpecialStore) {
            return undefined;
        }

        setSpecialStoreNow(Date.now());
        const timer = window.setInterval(() => setSpecialStoreNow(Date.now()), 1000);

        return () => window.clearInterval(timer);
    }, [showingSpecialStore]);

    const showActionButtons = !_.isEqual(containerState.boomerWearing, containerState.boomerReset);

    const onSelectedCategory = (selectedCategory) => {
        setContainerState(prevState => {
            return {
                ...prevState,
                boomerWearing: prevState.boomerReset,
                selectedCategory,
                selectedItem: null
            }
        });
    }

    const onSelectedItem = (selectedItem) => {
        setContainerState(prevState => {
            const previewBase = prevState.inventoryType === 'store' && prevState.selectedCategory === 'special_store'
                ? prevState.boomerReset
                : prevState.boomerWearing;
            return {
                ...prevState,
                boomerWearing: previewItem(previewBase, selectedItem),
                selectedItem: {
                    ...selectedItem,
                    wearing: true,
                    tileOwned: selectedItem.owned
                }
            }
        })
    }

    const onSelectedTile = (tile, tileOwned) => {
        setContainerState(prevState => {
            return {
                ...prevState,
                boomerWearing: previewTile(prevState.boomerWearing, prevState.selectedItem, tile),
                selectedItem: {
                    ...prevState.selectedItem,
                    tile,
                    tileOwned
                }
            }
        });
    }

    const onRemoveItem = () => {
        setContainerState(prevState => {
            return {
                ...prevState,
                boomerWearing: removeItem(prevState.boomerWearing, prevState.selectedItem),
                selectedItem: {
                    ...prevState.selectedItem,
                    tile: -1
                }
            }
        });
    }

    const onSetBoomerImg = (boomerImgData) => {
        setContainerState(prevState => {
            return {
                ...prevState,
                boomerImgData
            }
        });
    }

    const onSwitchInventory = (type) => {
        setContainerState(prevState => {
            return {
                ...prevState,
                inventoryType: type,
                selectedCategory: BODY,
                boomerWearing: prevState.boomerReset,
                selectedItem: null
            }
        });
    }

    const onBuy = () => {
        if (containerState.selectedItem.price > availableGems) {
            showCantAffordAlert();
        } else {
            showBuyConfirm();
        }
    }

    const onSave = (isPurchase) => {
        const { boomerWearing, selectedItem } = containerState;
        const backgroundOnly = selectedItem.type === 'backdrop';

        const candidates = _.uniqBy([
            ...avatarItems,
            ...(allItems || []).filter(item => item.price === 0),
            ...(isPurchase || (!showingSpecialStore && selectedItem.price === 0) ? [selectedItem] : [])
        ], item => String(item.id));
        const items = candidates.filter(item => backgroundOnly ? item.type === 'backdrop' : item.type !== BODY && item.type !== 'backdrop').map(item => {
            const wearingItem = isWearing(boomerWearing, item);
            if (!wearingItem) return null;

            return {
                ...(item.xmlId ? { xmlId: item.xmlId } : { avatarItemId: item.id }),
                gender: item.gender,
                type: item.type,
                xOffset: item.xOffset,
                yPos: item.yPos,
                tile: Number(wearingItem.tile || 1)
            };
        });

        if (!backgroundOnly) items.push({
            xmlId: `${boomerWearing.body.body_id}`,
            gender: "female",
            type: BODY,
            xOffset: null,
            yPos: null,
            tile: 1
        });

        const newItems = [];

        items.forEach(item => {
            if (item) {
                newItems.push({ ...item, gender: getGender(item.gender), type: getType(item.type) })
            }
        })

        setContainerState(prevState => {
            return {
                ...prevState,
                saving: true
            }
        });

        client.mutate({
            mutation: SAVE_BOOMER_ITEMS,
            refetchQueries: backgroundOnly ? [{ query: BOOMER_STORE_QUERY }] : [
                { query: BOOMER_STORE_QUERY },
                { query: GET_BOOMER_QUERY }
            ],
            variables: {
                items: _.compact(newItems),
                data: backgroundOnly ? '' : containerState.boomerImgData,
                backgroundOnly
            }
        }).then(({ data }) => {
            const errors = data.saveCurrentAvatarItems.errors;
            if (errors && errors.length) throw new Error(errors.join(', '));
            setContainerState(prevState => {
                return {
                    ...prevState,
                    boomerReset: containerState.boomerWearing,
                    saving: false,
                    selectedItem: {
                        ...selectedItem,
                        owned: true,
                        tileOwned: true
                    }
                }
            });
            setCurrentUser({
                ...currentUser,
                availableGems: data.saveCurrentAvatarItems.student.availableGems,
                backgroundImageUrl: data.saveCurrentAvatarItems.student.backgroundImageUrl,
                ...(!backgroundOnly ? { boomerAvatarUrl: updateQueryStringParameter(data.saveCurrentAvatarItems.student.boomerAvatarUrl, 'v', Math.floor(Math.random() * 7000)) } : {}),
            })
        }).catch(showStoreError);

    }

    const showStoreError = (error) => {
        setContainerState(previous => ({ ...previous, saving: false }));
        confirmAlert({
            customUI: ({ onClose }) => (
                <Alert
                    message={error.message}
                    onClose={onClose}
                    title="Unable to complete this action"
                    className="store__purchase-overlay"
                />
            )
        });
    };

    const updateQueryStringParameter = (uri, key, value) => {
        if (!uri) return uri;

        var re = new RegExp("([?&])" + key + "=.*?(&|$)", "i");
        var separator = uri.indexOf('?') !== -1 ? "&" : "?";
        if (uri.match(re)) {
            return uri.replace(re, '$1' + key + "=" + value + '$2');
        }
        else {
            return uri + separator + key + "=" + value;
        }
    }

    const onReset = () => {
        setContainerState(prevState => {
            return {
                ...prevState,
                boomerWearing: prevState.boomerReset,
                selectedItem: null
            }
        });
    }

    const showBuyConfirm = () => {
        const { selectedItem } = containerState;

        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <Confirm
                        cancelMessage={"Cancel"}
                        className={"store__purchase-overlay"}
                        message={<span className="store__purchase-overlay-message">Are you sure you want to purchase this item for <img className="store__purchase-overlay-coins" alt="Coins" src={Coins} />{selectedItem.price}?</span>}
                        onClose={onClose}
                        onConfirm={confirmPurchase}
                        title={"Confirm Purchase"}
                    >
                        <span className="store__purchase-overlay-message">Are you sure you want to purchase this item for: <img className="store__purchase-overlay-coins" alt="Coins" src={Coins} />{selectedItem.price}?</span>
                    </Confirm>
                );
            }
        });
    }

    const showCantAffordAlert = () => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <Alert
                        message={"You don't have enough gems for this item!"}
                        onClose={onClose}
                        title={"Uh oh!"}
                        className={"store__purchase-overlay"}
                    />
                )
            }
        });
    }

    const confirmPurchase = () => {
        const { selectedItem } = containerState;
        client.mutate({
            mutation: selectedItem.xmlId ? BUY_BOOMER_ITEM : BUY_BOOMER_ITEM_BY_ID,
            variables: selectedItem.xmlId ? {
                itemXmlId: selectedItem.xmlId,
                gender: getGender(selectedItem.gender),
                itemType: getType(selectedItem.type),
                tileId: parseInt(selectedItem.tile)
            } : {
                avatarItemId: selectedItem.id,
                gender: getGender(selectedItem.gender),
                itemType: getType(selectedItem.type),
                tileId: parseInt(selectedItem.tile)
            },
            awaitRefetchQueries: true,
            refetchQueries: () => [
                { query: GEMS_QUERY },
                { query: BOOMER_STORE_QUERY }
            ]
        }).then(({ data }) => {
            const errors = data.buyAvatarItem.errors;
            if (errors && errors.length) throw new Error(errors.join(', '));
            if (selectedItem.type === 'backdrop') {
                const { currentStudent } = client.readQuery({ query: BOOMER_STORE_QUERY });
                setCurrentUser({
                    ...currentUser,
                    availableGems: currentStudent.availableGems,
                    backgroundImageUrl: currentStudent.backgroundImageUrl,
                });
                setContainerState(previous => ({
                    ...previous,
                    boomerReset: previous.boomerWearing,
                    saving: false,
                    selectedItem: { ...previous.selectedItem, owned: true, tileOwned: true },
                }));
            } else {
                onSave(true);
            }
        }).catch(showStoreError);

        setContainerState(prevState => {
            return {
                ...prevState,
                saving: true
            }
        });
    }

    const getGender = (gender) => {
        switch (gender) {
            case 'female':
                return 1
            case 'male':
                return 2
            case 'mixed':
                return 3
            default:
                return
        }
    }

    const getType = (type) => {
        switch (type) {
            case 'body':
                return 1
            case 'hair':
                return 2
            case 'top':
                return 3
            case 'bottom':
                return 4
            case 'accessory':
                return 5
            case 'pet':
                return 6
            case 'backdrop':
                return 7
            default:
                return
        }
    }

    const categoryTitle = {
        special_store: 'Special Store',
        body: 'Skins',
        hair: 'Hair',
        top: 'Tops',
        bottom: 'Bottoms',
        pet: 'Pets',
        accessory: 'Accessories',
        backdrop: 'Backdrop'
    }[containerState.selectedCategory];
    const specialStore = specialStoreData && specialStoreData.specialStoreOpening && specialStoreData.specialStoreOpening[0];
    const specialStoreStart = specialStore ? moment.utc(specialStore.startDate, 'YYYY-MM-DD', true) : null;
    const specialStoreEnd = specialStore ? moment.utc(specialStore.endDate, 'YYYY-MM-DD', true) : null;
    const specialStoreIsOpen = Boolean(
        specialStoreStart &&
        specialStoreEnd &&
        specialStoreStart.isValid() &&
        specialStoreEnd.isValid() &&
        moment.utc(specialStoreNow).isBetween(specialStoreStart, specialStoreEnd, undefined, '[)')
    );
    const specialStoreItems = specialStore ?
        sortItems(containerState.boomerWearing, avatarItems, specialStore.avatarItems || []) :
        [];
    const drawingItems = _.uniqBy(
        [...(allItems || []), ...specialStoreItems, ...avatarItems],
        item => String(item.id)
    );
    const selectedBackdrop = containerState.boomerWearing.items.find(item => item.type === 'backdrop');
    const backdropUrl = selectedBackdrop ? (Array.isArray(selectedBackdrop.url) ? selectedBackdrop.url[Number(selectedBackdrop.tile || 1) - 1] : selectedBackdrop.url) : '';
    const specialStoreStockTime = specialStoreIsOpen ?
        getStockTime(specialStoreEnd, specialStoreNow) :
        '000000';

    const actionButtons = showActionButtons && !containerState.saving ? (
        <div className="store__actions">
            <button onClick={() => onReset()} className="button button--outline">Cancel</button>
            {(containerState.selectedItem.price === 0 && !showingSpecialStore) || containerState.selectedItem.tileOwned || containerState.inventoryType === 'owned' ?
                <button disabled={containerState.selectedItem.type !== 'backdrop' && !containerState.boomerImgData} onClick={() => onSave()} className="button button--bordered">Save</button> :
                <button disabled={containerState.selectedItem.type !== 'backdrop' && !containerState.boomerImgData} onClick={() => onBuy()} className="button button--bordered">Buy</button>
            }
        </div>
    ) : null;

    return (
        <div className={`store store--${containerState.inventoryType}${showingSpecialStore ? ' store--special' : ''}`}>
            {
                containerState.saving && <div className="store__loading"><img src={SmallLoader} alt="Saving" /></div>
            }
            <div className="store__topbar">
                <button className="store__home" onClick={() => navigate('/')} aria-label="Go home">
                    <img src={Home} alt="" />
                </button>
                <div className="store__wallet">
                    <img src={Coins} className="store__coins" alt="Gems" />
                    <p className="store__bucks"><BoomerBucks /></p>
                    <p className="store__wallet-text">Gems</p>
                </div>
                <div className="store__tabs" role="tablist" aria-label="Boomer shop sections">
                    <button
                        className={`store__tab store__tab--items ${containerState.inventoryType === "owned" ? "store__tab--active" : ""}`}
                        onClick={() => onSwitchInventory("owned")}
                        role="tab"
                        aria-selected={containerState.inventoryType === "owned"}
                    >
                        <img src={BoomerItems} alt="Boomer Items" />
                    </button>
                    <button
                        className={`store__tab store__tab--boutique ${containerState.inventoryType === "store" ? "store__tab--active" : ""}`}
                        onClick={() => onSwitchInventory("store")}
                        role="tab"
                        aria-selected={containerState.inventoryType === "store"}
                    >
                        <img src={BoomerBoutique} alt="Boomer Boutique" />
                    </button>
                </div>
            </div>
            <div className="store__workspace">
                <div className="store__preview">
                    <div className="store__preview-heading">
                        {containerState.inventoryType === 'owned' ?
                            'Equip items you own to your boomer by clicking on them.' :
                            <img className="store__preview-logo" src={BoomerBoutique} alt="Boomer Boutique" />
                        }
                    </div>
                    <img src={SpotlightLeft} alt="" className="store__spotlight" />
                    <img src={SpotlightRight} alt="" className="store__spotlight store__spotlight--right" />
                    {showingSpecialStore && !specialStoreIsOpen ?
                        <div className="special-store__empty-preview"><span className="store__stage" /></div> :
                        containerState.selectedCategory === 'backdrop' ?
                            <>
                                <svg className="store__backdrop-preview" viewBox="0 0 1254 1254" role="img" aria-label="Backdrop laptop preview">
                                    <defs>
                                        <clipPath id="pupil-backdrop-screen"><rect x="239" y="208" width="774" height="469" rx="30" /></clipPath>
                                    </defs>
                                    <image href={Backdrop} width="1254" height="1254" />
                                    {backdropUrl && <image href={backdropUrl} x="239" y="208" width="774" height="469" preserveAspectRatio="xMidYMid slice" clipPath="url(#pupil-backdrop-screen)" />}
                                </svg>
                            </> :
                            drawingItems.length > 0 &&
                            <div className="store__boomer-preview">
                                <DrawBoomer
                                    boomerData={containerState.boomerWearing}
                                    setBoomerImage={onSetBoomerImg}
                                    allItems={drawingItems}
                                />
                                <span className="store__stage" />
                            </div>
                    }
                </div>
                <div className="store__controls">
                    <Categories inventoryType={containerState.inventoryType} onSelectCategory={onSelectedCategory} selectedCategory={containerState.selectedCategory} />
                    {showingSpecialStore ?
                        <SpecialStore
                            actions={actionButtons}
                            error={specialStoreError}
                            isOpen={specialStoreIsOpen}
                            items={specialStoreItems}
                            loading={specialStoreLoading || (!specialStoreData && !specialStoreError)}
                            onSelectItem={onSelectedItem}
                            stockTime={specialStoreStockTime}
                            selectedId={containerState.selectedItem?.id}
                        /> :
                        <div className="store__items">
                            <h2 className="store__items-title">{categoryTitle}</h2>
                            {actionButtons}
                            <ul className={`store__items-list ${showActionButtons && !containerState.saving ? 'store__items-list--padded' : ''}`}>
                                {containerState.selectedItem && containerState.selectedItem.type !== BODY ?
                                    containerState.inventoryType === 'owned' ?
                                        <OwnedTiles
                                            item={containerState.selectedItem}
                                            onRemoveItem={onRemoveItem}
                                            onSelectTile={onSelectedTile}
                                        /> :
                                        <Tiles
                                            item={containerState.selectedItem}
                                            onRemoveItem={onRemoveItem}
                                            onSelectTile={onSelectedTile}
                                        /> :
                                    <Items
                                        setAllItems={setAllItems}
                                        inventoryType={containerState.inventoryType}
                                        onSelectItem={onSelectedItem}
                                        ownedItems={avatarItems}
                                        selectedCategory={containerState.selectedCategory}
                                        wearing={containerState.boomerWearing}
                                    />
                                }
                            </ul>
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

export default Container
